import { saveAsNewTemplateThunk } from '@/common/reducers/CampaignThunks'
import { useAppDispatch } from '@common-reducers/hooks/store.hook'
import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/common/components/dialog'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common/config/constants'

type Props = {
    isSaveNewTemplateModalOpen: boolean;
    onClose: () => void;
    /** Called after a successful save — the redesign shell routes back to the
     *  origin (e.g. the wizard) instead of stranding the user on the editor. */
    onSaved?: () => void;
}

export default function SaveNewTemplateModal({
    isSaveNewTemplateModalOpen,
    onClose,
    onSaved,
}: Props) {
    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const [templateName, setTemplateName] = useState('')
    const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';

    const handleCloseModal = () => {
        onClose()
        setTemplateName('')
    }

    const handleSaveTemplate = () => {
        if (!templateName.trim()) return;

        // Create a new template with the specified name
        dispatch(saveAsNewTemplateThunk(templateName.trim()))

        handleCloseModal()
        // Return to the origin (e.g. the wizard) under the redesign shell.
        onSaved?.()
    }

    const handleDialogOpenChange = (open: boolean) => {
        if (!open) {
            handleCloseModal()
        }
    }

    return (
        <Dialog open={isSaveNewTemplateModalOpen} onOpenChange={handleDialogOpenChange}>
            <DialogContent
                dir={isRtl ? 'rtl' : 'ltr'}
                className="max-w-[440px] p-0"
                style={{ zIndex: 22000 }}
            >
                <DialogHeader className="px-8 pt-6 pb-2 flex-shrink-0 text-start sm:text-start">
                    <DialogTitle className="text-xl font-semibold text-slate-800 dark:text-slate-100">
                        {t('messageEditor.saveTemplateModal.title')}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        {t('messageEditor.saveTemplateModal.title')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4 px-8 pb-6">
                    <input
                        autoFocus
                        id="template-name"
                        type="text"
                        dir={isRtl ? 'rtl' : 'ltr'}
                        placeholder={t('messageEditor.saveTemplateModal.placeholder')}
                        value={templateName}
                        onChange={(event) => setTemplateName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && templateName.trim()) {
                                handleSaveTemplate()
                            }
                        }}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-sky-400 focus:bg-white dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-100 dark:focus:bg-slate-800"
                    />

                    <div className="flex items-center justify-end gap-2">
                        <Button type="button" variant="ghost" onClick={handleCloseModal}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            type="button"
                            disabled={!templateName.trim()}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleSaveTemplate()
                            }}
                            className="bg-sky-500 text-white hover:bg-sky-600"
                        >
                            {t('messageEditor.saveTemplateModal.saveButton')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
